import { Box, type BoxProps, CssBaseline } from "@mui/material";
import { useSetAtom } from "jotai";
import { type FC, useEffect } from "react";
import { Toaster } from "react-hot-toast";
import { Outlet, useLocation } from "react-router";
import { ThemeProvider } from "../../../styles/theme/theme-provider";
import { type DisplayTheme, SettingsConsumer } from "../../../utils/contexts/settings-context";
import { Sidebar } from "./sidebar/sidebar";
import { sidebarOpenAtom } from "./sidebar/store";
import { layoutAtom } from "./store";

export const ApplicationLayout: FC<{
  themeOverride?: DisplayTheme;
  layout?: "content" | "default";
  slots?: {
    root?: {
      props?: BoxProps;
    };
    header?: {
      props?: BoxProps;
    };
  };
}> = ({ themeOverride, slots, layout = "default" }) => {
  const setLayout = useSetAtom(layoutAtom);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    setLayout(layout);
    return () => {
      document.body.style.overflow = "auto";
      setLayout("default");
    };
  }, [layout, setLayout]);

  const { pathname } = useLocation();
  const setSidebarOpen = useSetAtom(sidebarOpenAtom);

  useEffect(() => {
    setSidebarOpen(false);
  }, [setSidebarOpen, pathname]);

  return (
    <SettingsConsumer>
      {({ settings }) => (
        <ThemeProvider mode={themeOverride ?? settings.theme}>
          <CssBaseline />
          <Toaster />
          <Sidebar variant="temporary" />
          <AppRoot {...slots?.root?.props}>
            <Outlet />
          </AppRoot>
        </ThemeProvider>
      )}
    </SettingsConsumer>
  );
};

const AppRoot: FC<BoxProps> = ({ children, sx = [], ...props }) => (
  <Box
    className="App-root"
    sx={[
      {
        "--sb-thumb-width": "80px",
        bgcolor: "background.paper",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
      },
      ...(sx instanceof Array ? sx : [sx]),
    ]}
    {...props}
  >
    {children}
  </Box>
);
